import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, BookOpenCheck, ClipboardCheck, GraduationCap, Timer } from 'lucide-react';
import { Badge, Button, ErrorState, LoadingState } from '../components/ui';
import { loadPublishedTest } from '../lib/studentTestApi';

export function StudentTestInfo() {
  const { testId = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const returnCandidate = (location.state as { returnTo?: unknown } | null)?.returnTo;
  const returnTo = typeof returnCandidate === 'string' && /^\/courses\/[A-Za-z0-9_-]+\/tests(?:\?folder=[A-Za-z0-9_-]+)?$/.test(returnCandidate) ? returnCandidate : '/tests';
  const [data, setData] = useState<Awaited<ReturnType<typeof loadPublishedTest>>>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const value = await loadPublishedTest(testId);
        if (!active) return;
        if (!value) setError('This test is not available.'); else setData(value);
      } catch {
        if (active) setError('We could not load this test.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [testId]);

  if (loading) return <LoadingState />;
  if (error || !data) return <div className="detail-page"><ErrorState onRetry={() => window.location.reload()} /></div>;
  const { test, questions } = data;
  const totalMarks = questions.reduce((sum, rel) => sum + Number(rel.TestQuestionsPtr?.Marks ?? 0), 0) || Number(test.TotalMarks ?? 0);

  return <div className="detail-page">
    <Link to={returnTo} className="back-link">← {returnTo === '/tests' ? 'All tests' : 'Course tests'}</Link>
    <section className="detail-hero">
      <div className="test-hero-icon"><ClipboardCheck size={30} /></div>
      <div>
        <Badge tone={test.TestPaidType === 'Free' ? 'green' : 'teal'}>{test.TestPaidType ?? 'Paid'} test</Badge>
        <h1>{test.TestTitle}</h1>
        <p>{test.CourseTitle ?? 'AyurGuide assessment'}</p>
      </div>
    </section>
    <section className="test-info-grid">
      <div><ClipboardCheck size={18} /><strong>{questions.length}</strong><span>Questions</span></div>
      <div><Timer size={18} /><strong>{Number(test.Time ?? 60)} min</strong><span>Duration</span></div>
      <div><GraduationCap size={18} /><strong>{totalMarks}</strong><span>Total marks</span></div>
    </section>
    <section className="detail-section">
      <h2>Instructions</h2>
      <ul className="instructions-list">
        <li>The timer starts as soon as you press Start and runs even if you leave the page.</li>
        <li>Select an option to answer; select it again to clear your answer.</li>
        <li>Use Mark for review to flag questions you want to revisit before submitting.</li>
        <li>Negative marking applies where the test defines it — wrong answers can reduce your score.</li>
        <li>Your attempt is submitted automatically when the timer reaches zero.</li>
      </ul>
    </section>
    <section className="test-mode-row">
      <Button icon={ArrowRight} onClick={() => navigate(`/tests/${test.objectId}/take?mode=exam`)}>Start exam mode</Button>
      <Button variant="soft" icon={BookOpenCheck} onClick={() => navigate(`/tests/${test.objectId}/take?mode=practice`)}>Practice mode</Button>
    </section>
    <p className="mode-hint">Exam mode submits a scored result to your history. Practice mode shows instant feedback and saves nothing.</p>
  </div>;
}
